import { dateFormat } from "./Date.util";
import * as XLSX from 'xlsx';

function deepCopy(obj: any): any {
    return JSON.parse(JSON.stringify(obj));
}

function pageChanged(page: number, search: any, setParams: Function, setSearch: Function): void {
    let curSearch = deepCopy(search);
    curSearch.page = page;

    setParams((params) => ({
        ...params,
        search: curSearch,
    }));

    setSearch(true);
}

function handleChange(e: any, name: string, setParams: Function, params: any): void {
    // console.log('handleChange', e, name);

    // string , number 등 primitive 인 경우
    if (!e.target) {
        applyParams(e, name, setParams, params);

        // 체크박스 인 경우
    } else if (e.target.type == 'checkbox') {
        setParams(() => ({
            ...params,
            [name]: e.target.checked,
        }));

        // event 객체인 경우
    } else {
        applyParams(e.target.value, name, setParams, params);
    }
}

function applyParams(val: any, name: string, setParams: Function, params: any): any {
    const depth = name.split('.').length;

    if (depth == 4) {
        let parentContainer = name.split('.')[0];
        let container = name.split('.')[1];
        let group = name.split('.')[2];
        let key = name.split('.')[3];

        let newParentContainer = params[parentContainer];

        let newContainer = newParentContainer[container];

        let newObj = newContainer[group];

        newObj[key] = val;

        setParams(() => ({
            ...params,
            [parentContainer]: newParentContainer,
        }));
    } else if (depth == 3) {
        let container = name.split('.')[0];
        let group = name.split('.')[1];
        let key = name.split('.')[2];

        let newContainer = params[container];

        let newObj = newContainer[group];

        newObj[key] = val;

        setParams(() => ({
            ...params,
            [container]: newContainer,
        }));
    } else if (depth == 2) {
        let group = name.split('.')[0];

        // console.log('group', group);
        let key = name.split('.')[1];

        let newObj = params[group];
        newObj[key] = val;

        setParams(() => ({
            ...params,
            [group]: newObj,
        }));
    } else {
        setParams(() => ({
            ...params,
            [name]: val,
        }));
    }
}

function handleApiError(e: any): void {
    if (e.code == 'ERR_CANCELED' /*사용자 요청 취소*/) {
        return;
    }

    if (e.code == 'ERR_BAD_REQUEST' /*401*/ && e.response.status == 401 && !e.response.data.message) {
        return;
    }

    // console.log(e, e.code);

    if (e.response && e.response?.data && e.response?.data?.message) {
        alert(e.response.data.message);
    } else {
        alert(e.response.data.error);
    }
}

// 정규 표현식을 사용하여 이메일 주소를 검증
function isValidEmail(emailString: string): boolean {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;

    return emailRegex.test(emailString);
}

// 정규 표현식을 사용하여 숫자를 검증
function isValidNumber(numberString: string): boolean {
    const numberRegex = /^\d*$/;

    return numberRegex.test(numberString);
}

// 정규 표현식을 사용하여 문자열에 공백이 포함되었는지 검증
function isValidBlank(keyword: string): boolean {
    const numberRegex = /\s/;

    return numberRegex.test(keyword);
}

// 정규 표현식을 사용하여 yyyy-mm-dd 형식인지 확인
function isValidBirthday(dateString: string) {
    var regex = /^\d{4}-\d{2}-\d{2}$/;

    if (!regex.test(dateString)) {
        return false;
    }

    return true;
}

// 원화 단위로 표시하는 함수
function formatToKRW(amount: number): string {
    return new Intl.NumberFormat('ko-KR').format(amount);
}

function getDateAgo(days: number): string {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return dateFormat(date, 'YYYY-MM-DD');
}

function getCurrentDate(): string {
    return dateFormat(new Date(), 'YYYY-MM-DD');
}

function getFirstDayOfMonth(): string {
    const date = new Date();
    date.setDate(1);
    return dateFormat(date, 'YYYY-MM-DD');
}

/**
 * 엑셀 파일로 데이터를 내보내는 함수
 * @param data - 엑셀로 내보낼 데이터 배열
 * @param filename - 저장할 파일명 (확장자 포함)
 * @param sheetName - 워크시트 이름 (선택사항)
 */
function exportToExcel(data: any[], filename: string, sheetName: string = 'Sheet1'): void {
    try {
        // 데이터가 비어있는 경우 처리
        if (!data || data.length === 0) {
            alert('내보낼 데이터가 없습니다.');
            return;
        }

        // 워크북 생성
        const workbook = XLSX.utils.book_new();

        // 데이터를 워크시트로 변환
        const worksheet = XLSX.utils.json_to_sheet(data);

        // 컬럼 너비 자동 조정
        const columnWidths = [];
        const headers = Object.keys(data[0]);

        headers.forEach((header, index) => {
            // 헤더 길이와 데이터 길이 중 최대값 계산
            let maxLength = header.length;

            data.forEach(row => {
                const cellValue = String(row[header] || '');
                if (cellValue.length > maxLength) {
                    maxLength = cellValue.length;
                }
            });

            // 최소 10, 최대 50으로 제한
            columnWidths.push({
                wch: Math.min(Math.max(maxLength + 2, 10), 50)
            });
        });

        worksheet['!cols'] = columnWidths;

        // 헤더 스타일 적용 (선택사항)
        const headerRange = XLSX.utils.decode_range(worksheet['!ref']);
        for (let col = headerRange.s.c; col <= headerRange.e.c; col++) {
            const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
            if (worksheet[cellAddress]) {
                worksheet[cellAddress].s = {
                    font: { bold: true },
                    fill: { fgColor: { rgb: "FFCCCCCC" } },
                    alignment: { horizontal: "center" }
                };
            }
        }

        // 워크시트를 워크북에 추가
        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

        // 파일 다운로드
        XLSX.writeFile(workbook, filename);

        console.log(`엑셀 파일 다운로드 완료: ${filename}`);

    } catch (error) {
        console.error('엑셀 다운로드 중 오류 발생:', error);
        alert('엑셀 다운로드 중 오류가 발생했습니다.');
    }
}

/**
 * 복수 시트를 가진 엑셀 파일 내보내기
 * @param sheetsData - 시트별 데이터 객체 { sheetName: data[] }
 * @param filename - 저장할 파일명
 */
function exportToExcelMultiSheet(sheetsData: { [sheetName: string]: any[] }, filename: string): void {
    try {
        const workbook = XLSX.utils.book_new();

        Object.entries(sheetsData).forEach(([sheetName, data]) => {
            if (data && data.length > 0) {
                const worksheet = XLSX.utils.json_to_sheet(data);

                // 컬럼 너비 자동 조정
                const columnWidths = [];
                const headers = Object.keys(data[0]);

                headers.forEach((header) => {
                    let maxLength = header.length;

                    data.forEach(row => {
                        const cellValue = String(row[header] || '');
                        if (cellValue.length > maxLength) {
                            maxLength = cellValue.length;
                        }
                    });

                    columnWidths.push({
                        wch: Math.min(Math.max(maxLength + 2, 10), 50)
                    });
                });

                worksheet['!cols'] = columnWidths;
                XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
            }
        });

        XLSX.writeFile(workbook, filename);
        console.log(`멀티시트 엑셀 파일 다운로드 완료: ${filename}`);

    } catch (error) {
        console.error('멀티시트 엑셀 다운로드 중 오류 발생:', error);
        alert('엑셀 다운로드 중 오류가 발생했습니다.');
    }
}

/**
 * CSV 파일로 데이터를 내보내는 함수
 * @param data - CSV로 내보낼 데이터 배열
 * @param filename - 저장할 파일명 (확장자 포함)
 */
function exportToCSV(data: any[], filename: string): void {
    try {
        if (!data || data.length === 0) {
            alert('내보낼 데이터가 없습니다.');
            return;
        }

        // CSV 문자열 생성
        const headers = Object.keys(data[0]);
        const csvContent = [
            headers.join(','), // 헤더
            ...data.map(row =>
                headers.map(header => {
                    const value = row[header] || '';
                    // 쉼표나 따옴표가 포함된 경우 따옴표로 감싸기
                    if (String(value).includes(',') || String(value).includes('"')) {
                        return `"${String(value).replace(/"/g, '""')}"`;
                    }
                    return value;
                }).join(',')
            )
        ].join('\n');

        // BOM 추가 (한글 깨짐 방지)
        const BOM = '\uFEFF';
        const blob = new Blob([BOM + csvContent], {
            type: 'text/csv;charset=utf-8;'
        });

        // 다운로드 링크 생성 및 클릭
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        console.log(`CSV 파일 다운로드 완료: ${filename}`);

    } catch (error) {
        console.error('CSV 다운로드 중 오류 발생:', error);
        alert('CSV 다운로드 중 오류가 발생했습니다.');
    }
}

export default {
    pageChanged,
    handleChange,
    handleApiError,
    deepCopy,
    isValidEmail,
    isValidNumber,
    isValidBlank,
    isValidBirthday,
    formatToKRW,
    getDateAgo,
    getCurrentDate,
    getFirstDayOfMonth,
    exportToExcel,
    exportToExcelMultiSheet,
    exportToCSV,
};